/**
 * TasksPanelList Component
 *
 * Displays list of tasks with infinite scroll support via PaginatedList.
 */

import { useCallback } from 'react';
import { IconAlertTriangle, IconClipboardOff } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/components/common/EmptyState';
import { PaginatedList } from '@/components/ui/PaginatedList';
import { TaskCard } from '../../../sidebar/TaskCard';
import type { TaskListItem } from '@/types';
import type { TasksPanelListProps } from '../TasksPanel.types';
import styles from '../TasksPanel.module.css';

const NOOP = (): void => {};

export const TasksPanelList = ({
  tasks,
  loading = false,
  hasLoaded = false,
  error,
  onRetry,
  hasNextPage = false,
  onLoadMore,
}: TasksPanelListProps) => {
  const { t } = useTranslation();

  const renderTaskItem = useCallback(
    (task: TaskListItem) => <TaskCard key={task.id} task={task} />,
    []
  );

  const keyExtractor = useCallback((task: TaskListItem) => task.id, []);

  const renderEmpty = useCallback(
    () => (
      <EmptyState
        icon={<IconClipboardOff size={48} />}
        title={t('tasks.noTasksFound')}
        description={t('tasks.tryAdjustingFilters')}
      />
    ),
    [t]
  );

  const renderError = useCallback(
    (_errorMessage: string) => (
      <EmptyState
        icon={<IconAlertTriangle size={48} />}
        title={t('tasks.loadError')}
        description={t('tasks.loadErrorDescription')}
        action={
          onRetry
            ? {
                label: t('common.retry'),
                onClick: onRetry,
                variant: 'outline',
              }
            : undefined
        }
      />
    ),
    [t, onRetry]
  );

  return (
    <div className={styles.listSection}>
      <PaginatedList<TaskListItem>
        data={tasks}
        renderItem={renderTaskItem}
        keyExtractor={keyExtractor}
        isLoading={loading}
        hasLoaded={hasLoaded}
        error={error?.message}
        hasNextPage={hasNextPage}
        onLoadMore={onLoadMore ?? NOOP}
        mode="infinite"
        stateRenderers={{
          empty: renderEmpty,
          error: renderError,
        }}
        styling={{ gap: 'xs' }}
        aria-label={t('tasks.title')}
      />
    </div>
  );
};
